import * as Sentry from "@sentry/nextjs";
import { getCookie } from "./cookie";
import { AuthUserIdentity } from "../interfaces/SupplierInterface";

const IMPERSONATED_TAG = "impersonated";

/**
 * Attach the logged-in supplier's identity to Sentry so every error event
 * reported while they're signed in includes user id/email — matches the
 * existing Mixpanel `eventTracker({ auth: true })` identify pattern in
 * utils/mixPanel.ts, but for Sentry.
 *
 * Call with the freshly-returned `user` object right after a successful
 * login/impersonation (before the "auth" cookie read-back would even be
 * needed), or with no argument to read the current "auth" cookie (e.g. on
 * app bootstrap, to cover an already-logged-in session on page load/refresh).
 *
 * Also (re)applies the `impersonated` tag from the "isUserImpersonate" cookie
 * — the same source of truth PageHeader/DashboardLayout already use — so an
 * impersonated session stays tagged across a page refresh, not only on the
 * call that started it.
 *
 * With no usable identity this clears the scope instead of leaving it alone,
 * so the Sentry context always reflects the current session — see below.
 *
 * Never includes accessToken or any other secret/token field — only the
 * identifiers Sentry's User interface expects.
 */
export const identifySentryUser = (user?: AuthUserIdentity) => {
  try {
    const auth: AuthUserIdentity | undefined = user ?? getCookie("auth");
    if (!auth?.id) {
      // Clear rather than return: the Sentry scope outlives client-side
      // navigation, so a stale user (and `impersonated` tag) from earlier in
      // this same SPA session would otherwise survive an identify that found
      // no usable id, and misattribute every later event to them.
      clearSentryUser();
      return;
    }

    Sentry.setUser({
      id: String(auth.id),
      email: auth.email,
      username: auth.displayName || auth.firstName,
    });

    setImpersonatedTag(getCookie("isUserImpersonate") ? true : undefined);
  } catch (error) {
    // Identifying the user must never break the app.
  }
};

/**
 * Clear the Sentry user context on logout, so errors after sign-out aren't
 * misattributed to the previous session's user — and drop the `impersonated`
 * tag with it, so an admin ending an impersonated session doesn't leave every
 * later event tagged for the rest of the page's lifetime.
 */
export const clearSentryUser = () => {
  try {
    Sentry.setUser(null);
    setImpersonatedTag(undefined);
  } catch (error) {
    // no-op
  }
};

/**
 * `undefined` rather than `false`: the SDK exposes no `removeTag()`, and an
 * undefined tag value is dropped when the event is serialised — so a normal
 * session carries no `impersonated` tag at all rather than a noisy
 * `impersonated: false`.
 */
const setImpersonatedTag = (value: true | undefined) => {
  Sentry.setTag(IMPERSONATED_TAG, value);
};
